import { useQueryClient } from "@tanstack/react-query";
import { useNotification } from "app/NotificationProvider";
import { analytics, AnalyticsEvents } from "common/analytics";
import EventFileUpload from "common/components/EventFileUpload";
import EventFormDialog from "common/components/EventFormDialog";
import { useTranslation } from "common/locales";
import { queryKeys } from "common/queryKeys";
import { ReseedSingleStep } from "features/bulkReseedEventInput/EnterReseedEvents";
import { NameAndGuid } from "features/general/NameAndGuidMultiSelectDropdown";
import {
  AMOUNT_INPUT_MODE,
  Field,
  getReseedTypeFromMethod,
  GRASS_TYPE,
  RESEED_TOTAL_AMOUNT_UNIT,
  ReseedEvent,
  SEEDING_METHOD,
  useUpdateReseedEvent,
} from "model";
import { Dispatch, SetStateAction, useState } from "react";

export default function UpdateReseedEvent({
  fields,
  reseedEvent,
  dialogOpen,
  setDialogOpen,
}: {
  fields: Field[];
  reseedEvent: ReseedEvent;
  dialogOpen: boolean;
  setDialogOpen: Dispatch<SetStateAction<boolean>>;
}) {
  const queryClient = useQueryClient();
  const { notify } = useNotification();
  const { t } = useTranslation();
  const updateReseedEventMutation = useUpdateReseedEvent();

  const handleClose = () => {
    setDialogOpen(false);
  };

  const [newGrassType, setNewGrassType] = useState<GRASS_TYPE | undefined>(
    reseedEvent.newGrassType
  );

  const [grassTypeError, setGrassTypeError] = useState<boolean>(false);

  const [seedingMethod, setSeedingMethod] = useState<
    SEEDING_METHOD | undefined
  >(reseedEvent.seedingMethod);

  const [seedingMethodError, setSeedingMethodError] = useState<boolean>(false);

  const [selectedFields, setSelectedFields] = useState<NameAndGuid[]>(
    fields.map((field: Field) => ({ name: field.name, guid: field.guid }))
  );

  const [notes, setNotes] = useState<string>(reseedEvent.note ?? "");

  const [reseedDate, setReseedDate] = useState<Date | null>(
    new Date(Date.parse(reseedEvent.reseedDate))
  );

  const [reseedDateError, setReseedDateError] = useState<boolean>(false);

  const [fieldsSelectInputError, setFieldsSelectInputError] =
    useState<boolean>(false);

  const [seedingRate, setSeedingRate] = useState<number>(
    reseedEvent.seedingRate ?? ("" as unknown as number)
  );

  const [amountInputMode, setAmountInputMode] = useState<AMOUNT_INPUT_MODE>(
    reseedEvent.amountInputMode ?? AMOUNT_INPUT_MODE.RATE
  );

  const [totalAmount, setTotalAmount] = useState<number>(
    reseedEvent.totalAmount ?? ("" as unknown as number)
  );

  const [totalAmountUnit, setTotalAmountUnit] =
    useState<RESEED_TOTAL_AMOUNT_UNIT>(
      reseedEvent.totalAmountUnit ?? RESEED_TOTAL_AMOUNT_UNIT.KG
    );

  const [productName, setProductName] = useState<string>(
    reseedEvent.productName ?? ""
  );

  const [productCost, setProductCost] = useState<number>(
    reseedEvent.productCost ?? ("" as unknown as number)
  );

  const hasChanges = () => {
    return (
      newGrassType !== reseedEvent.newGrassType ||
      seedingMethod !== reseedEvent.seedingMethod ||
      notes !== (reseedEvent.note ?? "") ||
      reseedDate?.toISOString() !==
        new Date(Date.parse(reseedEvent.reseedDate)).toISOString() ||
      productName !== (reseedEvent.productName ?? "") ||
      productCost !== (reseedEvent.productCost ?? ("" as unknown as number)) ||
      seedingRate !== (reseedEvent.seedingRate ?? ("" as unknown as number)) ||
      amountInputMode !==
        (reseedEvent.amountInputMode ?? AMOUNT_INPUT_MODE.RATE) ||
      totalAmount !== (reseedEvent.totalAmount ?? ("" as unknown as number)) ||
      selectedFields.length !== fields.length ||
      selectedFields.some((sf, i) => sf.guid !== fields[i]?.guid)
    );
  };

  function validateRequired(): boolean {
    let valid = true;
    if (!newGrassType) {
      setGrassTypeError(true);
      valid = false;
    }
    if (!reseedDate) {
      setReseedDateError(true);
      valid = false;
    }
    if (!seedingMethod) {
      setSeedingMethodError(true);
      valid = false;
    }
    return valid;
  }

  async function handleSave() {
    if (!validateRequired()) return;
    if (!reseedDate || !newGrassType || !seedingMethod) return;

    if (seedingRate && seedingRate < 0) {
      return;
    }

    const fieldGuids = selectedFields.map(
      (selectedField: NameAndGuid) => selectedField.guid ?? ""
    );

    await updateReseedEventMutation.mutateAsync({
      reseedEvent: {
        fieldGuids,
        newGrassType: newGrassType,
        guid: reseedEvent.guid,
        note: notes ?? "",
        seedingMethod: seedingMethod,
        reseedDate: reseedDate.toISOString(),
        reseedType: getReseedTypeFromMethod(seedingMethod),
        seedingRate: seedingRate,
        totalAmount: (totalAmount as unknown) === "" ? undefined : totalAmount,
        totalAmountUnit: totalAmountUnit,
        amountInputMode: amountInputMode,
        productName: productName,
        productCost: (productCost as unknown) === "" ? undefined : productCost,
        productCostUnit: (productCost as unknown) === "" ? undefined : "GBP",
      },
    });

    // Invalidate field queries for affected fields
    await Promise.all(
      fieldGuids.map((fieldGuid) =>
        queryClient.invalidateQueries({
          queryKey: queryKeys.fields.detail(fieldGuid),
        })
      )
    );

    notify(t("editReseedEvent.success"));
    analytics.track(AnalyticsEvents.RESEED_EVENT_UPDATED);
    handleClose();
  }

  return (
    <EventFormDialog
      open={dialogOpen}
      onClose={handleClose}
      title={t("reseedEvent.heading")}
      onSave={handleSave}
      isDirty={hasChanges}
      cancelAnalyticsEvent={AnalyticsEvents.RESEED_EVENT_UPDATE_CANCELLED}
      steps={[
        {
          content: (
            <ReseedSingleStep
              reseedDate={reseedDate}
              setReseedDate={setReseedDate}
              reseedDateError={reseedDateError}
              setReseedDateError={setReseedDateError}
              selectedFields={selectedFields}
              setSelectedFields={setSelectedFields}
              fieldsSelectInputError={fieldsSelectInputError}
              setFieldsSelectInputError={setFieldsSelectInputError}
              enableFieldSelection={true}
              onSeedingMethodChange={(value) => {
                setSeedingMethod(value);
                if (value) {
                  setSeedingMethodError(false);
                } else {
                  setSeedingMethodError(true);
                }
              }}
              newGrassType={newGrassType}
              setNewGrassType={setNewGrassType}
              grassTypeError={grassTypeError}
              setGrassTypeError={setGrassTypeError}
              seedingMethod={seedingMethod}
              seedingMethodError={seedingMethodError}
              productName={productName}
              setProductName={setProductName}
              seedingRate={seedingRate}
              setSeedingRate={setSeedingRate}
              amountInputMode={amountInputMode}
              setAmountInputMode={setAmountInputMode}
              totalAmount={totalAmount}
              setTotalAmount={setTotalAmount}
              totalAmountUnit={totalAmountUnit}
              setTotalAmountUnit={setTotalAmountUnit}
              productCost={productCost}
              setProductCost={setProductCost}
              notes={notes}
              setNotes={setNotes}
              fileUploadSlot={
                <EventFileUpload farmEventGuid={reseedEvent.guid} />
              }
            />
          ),
        },
      ]}
    />
  );
}
